import type {
  PredefinedMessage,
  PredefinedMessageFormValues,
  UsePredefinedMessageMutationsArgs,
} from '@/types/predefined-messages';

import { CLOSED, EDIT } from '@/constants';
import { useCreatePredefinedMessage } from '@/hooks/api/use-predefined-message-mutations/create';
import { useDeletePredefinedMessage } from '@/hooks/api/use-predefined-message-mutations/delete';
import { useEditPredefinedMessage } from '@/hooks/api/use-predefined-message-mutations/edit';

export function usePredefinedMessageMutations({
  messageFormState,
  setMessageFormState,
  setMessages,
}: UsePredefinedMessageMutationsArgs) {
  const createMessage = useCreatePredefinedMessage();
  const editMessage = useEditPredefinedMessage();
  const deleteMessage = useDeletePredefinedMessage();

  function onCreateComplete(message: PredefinedMessage) {
    setMessages((prev) => [...prev, message]);
    setMessageFormState({ action: CLOSED });
  }

  function onEditComplete(message: PredefinedMessage) {
    setMessages((prev) =>
      prev.map((prevMessage) => (prevMessage.id === message.id ? message : prevMessage))
    );
    setMessageFormState({ action: CLOSED });
  }

  function onDeleteComplete(message: PredefinedMessage) {
    if (messageFormState.action === EDIT && messageFormState.id === message.id) {
      setMessageFormState({ action: CLOSED });
    }

    setMessages((prev) => prev.filter((m) => m.id !== message.id));
  }

  async function onCreateMessage(values: PredefinedMessageFormValues) {
    try {
      const response = await createMessage.mutateAsync(values);
      onCreateComplete(response);
    } catch (error) {
      console.error('Failed to add message:', error);
    }
  }

  async function onEditMessage(values: PredefinedMessageFormValues) {
    if (messageFormState.action !== EDIT) {
      return;
    }

    try {
      const response = await editMessage.mutateAsync({ id: messageFormState.id, values });
      onEditComplete(response);
    } catch (error) {
      console.error('Failed to edit message:', error);
    }
  }

  async function onDeleteMessage(message: PredefinedMessage) {
    try {
      await deleteMessage.mutateAsync(message.id);
      onDeleteComplete(message);
    } catch (error) {
      console.error('Failed to delete message:', error);
    }
  }

  return {
    onCreateMessage,
    onEditMessage,
    onDeleteMessage,
    deleteMessage: onDeleteMessage,
  };
}

export { useCreatePredefinedMessage } from '@/hooks/api/use-predefined-message-mutations/create';
export { useDeletePredefinedMessage } from '@/hooks/api/use-predefined-message-mutations/delete';
export { useEditPredefinedMessage } from '@/hooks/api/use-predefined-message-mutations/edit';
